import useWatchTime from 'src/hooks/useWatchTime';
import useSubtitle from 'src/hooks/useSubtitle';
import useSeek from 'src/hooks/useSeek';
import useUI from 'src/hooks/useUI';
import usePlayback from 'src/hooks/usePlayback';
import { isPictureInPictureSupported } from 'src/utils/browserUtils';
import useAutoplay from 'src/hooks/useAutoplay';
import VolumeControl from 'src/themes/cosmos/components/VolumeControl';
import SeekBar from 'src/themes/cosmos/components/SeekBar';
import SubtitleToggle from 'src/themes/cosmos/components/SubtitleToggle';
import TextTime from 'src/themes/cosmos/components/TextTime';
import PlayControl from 'src/themes/cosmos/components/PlayControl';
import PictureInPictureControl from 'src/themes/cosmos/components/PictureInPictureControl';
import SettingsButton from 'src/themes/cosmos/components/SettingsButton';
import AutoplayToggle from 'src/themes/cosmos/components/AutoplayToggle';
import FullScreenControl from 'src/themes/cosmos/components/FullScreenControl';
import RewindControl from 'src/themes/cosmos/components/RewindControl';
import ForwardControl from 'src/themes/cosmos/components/ForwardControl';
import classNames from 'classnames';
import useApplicationData from 'src/hooks/useApplicationData';
import { MEDIA_TYPE, TRANSMISSION_TYPE } from 'src/interfaces/enums';
import useMarker from 'src/hooks/useMarker';
import LiveControl from '../../components/LiveControl';
import styles from './styles.module.scss';

const BottomBarDesktop = () => {
  const { toggleSubtitle, subtitleEnabled, subtitles } = useSubtitle();

  const {
    bufferIndicatorPercent,
    timePercent,
    setWatchTime,
    currentTime,
    duration,
    forwardSeconds,
    backwardSeconds,
    isCloseToLiveEdge,
    seekToLiveEdge,
  } = useWatchTime();
  const { toggleAutoplayEnabled, enabled: autoplayEnabled } = useAutoplay();

  const { toggleFullscreen, fullscreen, isPictureInPictureEnabled, toggleSettings, togglePictureInPicture } = useUI();

  const { paused, togglePlay, volumePercent, setVolumePercent } = usePlayback();
  const { mediaType, transmissionType, isAutonextEnabled } = useApplicationData();
  const { onSeekInit, onSeekEnded } = useSeek();
  const { isMarkerOverlayVisible: isSideNotesOpen } = useMarker();

  const isVod = transmissionType === TRANSMISSION_TYPE.vod;
  const isVideo = mediaType === MEDIA_TYPE.video;

  return (
    <div
      className={classNames(styles.bottomBar, {
        [styles.sideNotesOpen]: isSideNotesOpen,
      })}
      data-testid='bottom-bar'
    >
      {isVod && (
        <div className={styles.seekbarContainer}>
          <TextTime time={currentTime} />
          <div className={styles.seekbar}>
            <SeekBar
              onSeeking={setWatchTime}
              bufferIndicatorPercent={bufferIndicatorPercent}
              currentPercent={timePercent}
              onSeekInit={onSeekInit}
              onSeekEnded={onSeekEnded}
            />
          </div>
          <TextTime time={duration - currentTime} />
        </div>
      )}
      <div className={styles.controls}>
        <div className={classNames(styles.columns, { [styles.isFullScreen]: fullscreen })}>
          {isVod ? (
            <>
              <PlayControl
                className={classNames(styles.desktopButton, styles.playControl)}
                paused={paused}
                onClick={togglePlay}
              />
              <RewindControl
                className={classNames(styles.seekTimeControls, styles.rewindControl)}
                onClick={backwardSeconds}
              />
              <ForwardControl
                className={classNames(styles.seekTimeControls, styles.forwardControl)}
                onClick={forwardSeconds}
              />
            </>
          ) : (
            <>
              <LiveControl active={isCloseToLiveEdge} className={styles.liveControl} onClick={seekToLiveEdge} />
              <PlayControl
                className={classNames(styles.desktopButton, styles.playControl)}
                paused={paused}
                onClick={togglePlay}
              />
            </>
          )}
          <VolumeControl className={styles.volumeControl} volumePercent={volumePercent} onChange={setVolumePercent} />
        </div>
        <div className={classNames(styles.columns, { [styles.isFullScreen]: fullscreen })}>
          {isVod && isAutonextEnabled && (
            <AutoplayToggle
              containerClassName={classNames(styles.autoplayToggle)}
              active={autoplayEnabled}
              onToggle={toggleAutoplayEnabled}
            />
          )}
          {subtitles && (
            <SubtitleToggle
              className={classNames(styles.desktopButton, styles.subtitleToggle)}
              isEnabled={subtitleEnabled}
              onClick={toggleSubtitle}
            />
          )}
          <SettingsButton
            className={classNames(styles.desktopButton, styles.settingsButton)}
            onClick={toggleSettings}
          />
          {isPictureInPictureSupported() && isVideo && (
            <PictureInPictureControl
              className={classNames(styles.desktopButton, styles.pictureInPictureControl)}
              onClick={togglePictureInPicture}
              isActive={isPictureInPictureEnabled}
            />
          )}
          <FullScreenControl
            className={classNames(styles.desktopButton, styles.fullScreenControl)}
            active={fullscreen}
            onClick={toggleFullscreen}
          />
        </div>
      </div>
    </div>
  );
};

export default BottomBarDesktop;
